import {
  type DocumentData,
  type DocumentSnapshot,
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  setDoc,
  updateDoc,
  where,
} from "firebase/firestore/lite";

import moment from "moment";
import { isDesktop, isMobileOnly, isTablet } from "react-device-detect";
import { db } from "../auth/firebase";
import { parseGeoData } from "../utils/helpers";

const createCommonIdeaObject = (
  ideaDocSnapshot: DocumentSnapshot<DocumentData>,
  userDocSnapshot: DocumentSnapshot<DocumentData>,
  project,
  comments: any[] = [],
  ideaMap
) => {
  if (!ideaDocSnapshot)
    throw new Error("IdeaDocSnapshot is missing in createCommonIdeaObject");
  if (!project) throw new Error("Project is missing in createCommonIdeaObject");
  if (!ideaDocSnapshot.exists())
    throw new Error("IdeaDocSnapshot does not exist in createCommonIdeaObject");


  return {
    ...ideaDocSnapshot.data(),
    comments,
    ideaId: ideaDocSnapshot.id,
    isAnonymous: userDocSnapshot?.data()?.isAnonymous || null,
    userHandle: userDocSnapshot?.data()?.handle || null,
    userImage: userDocSnapshot?.data()?.photoURL || null,
    topicLabel:
      ideaMap?.customTopicsObject?.[ideaDocSnapshot.data().topic]?.label,
    color: ideaMap?.customTopicsObject?.[ideaDocSnapshot.data().topic]?.color,
    // geoData:
    //   ideaDocSnapshot.data().geoDataType === "noLocation"
    //     ? ideaMap?.mapbounds
    //     : ideaDocSnapshot.data().geoData,

    geoData:
    ideaDocSnapshot.data()?.geoDataType === "noLocation"
      ? {
          type: "FeatureCollection",
          features: ideaMap.mapbounds.features.map((feature) => ({
            ...feature,
            properties: {
              ...feature.properties,
              drawType: "area",
            },
          })),
        }
      : ideaDocSnapshot.data()?.geoDataType === "location"
        ? null
        : {
            type: "FeatureCollection",
            features: parseGeoData(ideaDocSnapshot.data()?.geoData)?.features?.map(
              (feature) => ({
                ...feature,
                properties: {
                  ...feature.properties,
                  // ideaId: idea?.ideaId,
                  // title: idea?.title,
                  color: ideaMap?.customTopicsObject?.[ideaDocSnapshot.data()?.topic]?.color,
                },
              })
            ),
          },
  };
};

export const setIdea = async (ideaId, project, ideaMap) => {
  if (!ideaId) throw new Error("IdeaId is missing in setIdea");
  if (!project) throw new Error("Project is missing in setIdea");
  if (!project.organizationId)
    throw new Error("OrganizationId is missing in setIdea");
  if (!project.projectId) throw new Error("ProjectId is missing in setIdea");
  try {
    const docRef = doc(
      db,
      `organizations/${project.organizationId}/projects/${project.projectId}/ideas/${ideaId}`
    );
    const ideaDocSnapshot = await getDoc(docRef);
    if (!ideaDocSnapshot.exists()) {
      console.log("Idea has not been found in setIdea");
      return null;
    }

    // add userData
    // Try to get user from project participants first
    let userDocSnapshot;
    const participantDocRef = doc(
      db,
      `organizations/${project.organizationId}/projects/${project.projectId}/participants/${ideaDocSnapshot.data().userId}`
    );
    const participantDocSnapshot = await getDoc(participantDocRef);

    if (participantDocSnapshot.exists()) {
      userDocSnapshot = participantDocSnapshot;
    } else {
      // Fall back to users collection if not found in participants
      const userDocRef = doc(db, `users/${ideaDocSnapshot.data().userId}`);
      userDocSnapshot = await getDoc(userDocRef);
      if (!userDocSnapshot.exists())
        console.log("User has not been found based on idea in setIdea");
    }

    // add comments to the idea
    const commentRef = collection(db, "comments");
    const commentquery = query(
      commentRef,
      where("ideaId", "==", ideaId),
      orderBy("createdAt", "desc")
    );
    const commentquerySnapshot = await getDocs(commentquery);
    if (commentquerySnapshot.empty) {
      return createCommonIdeaObject(
        ideaDocSnapshot,
        userDocSnapshot,
        project,
        [],
        ideaMap
      );
    }

    const commentsArray: DocumentData[] = [];

    commentquerySnapshot.docs.forEach((comment) => {
      commentsArray.push({
        ...comment.data(),
        commentId: comment.id,
        title: comment.data().body,
      });
    });
    const getUserHandleAndPhotoURL = async (userId) => {
      // Try to get user from project participants first
      const participantDocRef = doc(
        db,
        `organizations/${project.organizationId}/projects/${project.projectId}/participants/${userId}`
      );
      const participantDocSnapshot = await getDoc(participantDocRef);

      if (participantDocSnapshot.exists()) {
        return {
          userHandle: participantDocSnapshot.data()?.handle,
          photoURL: participantDocSnapshot.data()?.photoURL,
          isAnonymous: participantDocSnapshot.data()?.isAnonymous,
        };
      }

      // Fall back to users collection if not found in participants
      const commentUserSnapshot = await getDoc(doc(db, `users/${userId}`));
      if (!commentUserSnapshot.exists()) {
        return {
          userHandle: null,
          photoURL: null,
          isAnonymous: null,
        };
      }
      return {
        userHandle: commentUserSnapshot.data()?.handle,
        photoURL: commentUserSnapshot.data()?.photoURL,
        isAnonymous: commentUserSnapshot.data()?.isAnonymous,
      };
    };

    const modifyCommentsArray = (commentsArray) => {
      return commentsArray.map(async (comment) => {
        const { userHandle, photoURL, isAnonymous } = await getUserHandleAndPhotoURL(
          comment.userId
        );
        return {
          ...comment,
          userHandle,
          photoURL,
          isAnonymous
        };
      });
    };
    const modifiedComments = await Promise.all(
      modifyCommentsArray(commentsArray)
    );

    return createCommonIdeaObject(
      ideaDocSnapshot,
      userDocSnapshot,
      project,
      modifiedComments,
      ideaMap
    );
  } catch (error) {
    throw new Error(`error in setIdea: ${error instanceof Error ? error.message : String(error)}`);
  }
};

export const postIdea = (
  formik,
  project,
  user,
  location,
  geoDataType,
  ideaId,
  map,
  ideaMap
) => {
  

  return new Promise(async (resolve, reject) => {
    try {
      const locationBased = geoDataType === "location";
      const drawn = geoDataType === "path" || geoDataType === "area";

      // Check if we're using a Cesium map
      const isCesiumMap = map && map.centerCoordinates;
      
      const locationData = {
        countryName: location?.countryName || project?.location?.countryName || null,
        countryId: location?.countryId || project?.location?.countryId || null,
        regionName: location?.regionName || project?.location?.regionName || null,
        regionId: location?.regionId || project?.location?.regionId || null,
        municipalityId:
          location.municipalityId || project.location?.municipalityId || null,
        municipalityName:
          location.municipalityName || project.location?.municipalityName || null,
        postcodeName: location?.postcodeName || null,
        postcodeId: location?.postcodeId || null,
        localityName: location?.localityName || null,
        localityId: location?.localityId || null,
        address: locationBased ? formik.values.address : null,
        lat: locationBased ? (isCesiumMap ? map.centerCoordinates.latitude : map?.getCenter().lat) : null,
        long: locationBased ? (isCesiumMap ? map.centerCoordinates.longitude : map?.getCenter().lng) : null,
        geoDataType,
        geoData: drawn ? JSON.stringify(formik.values.geoData) : null,
      };

      const systemData = {
        likeCount: 0,
        commentCount: 0,
        status: "None",
        ideaMapId: ideaMap.ideaMapId,
        projectId: project.projectId,
        organizationId: project.organizationId,
        createdAt: new Date().toISOString(),
        device: isMobileOnly
          ? "mobile"
          : isTablet
            ? "tablet"
            : isDesktop
              ? "desktop"
              : "other",
      };
      const userData: {
        userId: string;
        gender: string | null;
        age: number | null;
        userPostcode: string | null;
        utm_id?: string;
      } = {
        userId: user.userId,
        gender: user.gender || null,
        age:
          user.age !== ""
            ? moment()?.diff(moment(user.age, "YYYY"), "years")
            : null,
        userPostcode: user.postcode || null,
      };
      if (user.utm_id) userData.utm_id = user.utm_id;

      const ideaData = ideaId
        ? { ...formik.values, ...locationData }
        : { ...formik.values, ...locationData, ...userData, ...systemData };

      if (formik.values.weblinkTitle)
        ideaData.weblinkTitle = formik.values.weblinkTitle;
      if (formik.values.weblink) ideaData.weblink = formik.values.weblink;
      if (formik.values.contactTitle)
        ideaData.contactTitle = formik.values.contactTitle;
      if (formik.values.contact) ideaData.contact = formik.values.contact;

      if (ideaId) {
        const docRef = doc(
          db,
          `organizations/${project.organizationId}/projects/${project.projectId}/ideas/${ideaId}`
        );

        await updateDoc(docRef, ideaData);
      } else {
        const projectIdeasRef = collection(
          db,
          `organizations/${project.organizationId}/projects/${project.projectId}/ideas`
        );
        const newDocRef = doc(projectIdeasRef);
        ideaData.ideaId = newDocRef.id;

        await setDoc(newDocRef, ideaData);
      }

      const resIdea = {
        ...ideaData,
        color: ideaMap?.customTopicsObject[ideaData.topic].color,
        topicLabel: ideaMap?.customTopicsObject[ideaData.topic].label,
      };

      resolve(resIdea);
    } catch (error) {
      reject(new Error(`error in postIdeaFunc: ${error instanceof Error ? error.message : String(error)}`));
    }
  });
};
